import React, { useState, useRef, useEffect } from 'react';
import api from '@/utils/api';
import { useAppDispatch } from '@/redux/Store';
import { updateUser } from '@/redux/slices/authSlice';
import './TermsModal.scss';

// Bump every time the copy below changes materially so users are
// re-prompted to accept the new version. 1.1 adds the explicit
// wallet-key custody clause (§4a) for email-signup users, who may
// not realise the auto-generated wallet is entirely theirs to
// safeguard.
const TERMS_VERSION = '1.1';

const TermsModal: React.FC = () => {
  const dispatch = useAppDispatch();
  const [agreed, setAgreed] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);

  const handleScroll = () => {
    const el = contentRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop <= el.clientHeight + 40;
    if (atBottom) setHasScrolledToBottom(true);
  };

  useEffect(() => {
    // If content is short enough to not need scrolling, unlock immediately
    const el = contentRef.current;
    if (el && el.scrollHeight <= el.clientHeight) {
      setHasScrolledToBottom(true);
    }
  }, []);

  const handleAccept = async () => {
    if (!agreed || isSubmitting) return;
    setIsSubmitting(true);
    setError(null);
    try {
      await api.post('/auth/accept-terms');
      dispatch(updateUser({ requiresTermsAccept: false }));
    } catch {
      setError('Failed to record your agreement. Please try again.');
      setIsSubmitting(false);
    }
  };

  return (
    <div className="terms-overlay">
      <div className="terms-modal">
        <div className="terms-modal__header">
          <div className="terms-modal__icon">📋</div>
          <h2>Terms &amp; Conditions</h2>
          <p>Version {TERMS_VERSION} &mdash; Please read carefully before continuing</p>
        </div>

        <div
          className="terms-modal__content"
          ref={contentRef}
          onScroll={handleScroll}
        >
          <section>
            <h3>1. Acceptance of Terms</h3>
            <p>
              By accessing and using Klyx ("the Platform"), you agree to be bound by these Terms &amp;
              Conditions. If you do not agree to these terms, you must not use the Platform.
            </p>
          </section>

          <section>
            <h3>2. Nature of the Platform</h3>
            <p>
              Klyx is a decentralised NFT profile trading platform built on the Klever blockchain.
              The Platform allows users to create tokenised profiles, trade them as non-fungible
              tokens (NFTs), and interact with other users. All on-chain transactions are final and
              irreversible once confirmed.
            </p>
          </section>

          <section>
            <h3>3. Eligibility</h3>
            <p>
              You must be at least 18 years of age to use the Platform. By using Klyx, you represent
              and warrant that you meet this requirement and have the legal capacity to enter into
              binding agreements.
            </p>
          </section>

          <section>
            <h3>4. User Accounts &amp; Wallets</h3>
            <p>
              You are solely responsible for maintaining the security of your account credentials and
              wallet private keys. Klyx cannot recover lost keys or reverse unauthorised transactions.
              You must notify us immediately of any suspected unauthorised access to your account.
            </p>
          </section>

          <section>
            <h3>4a. Wallet Custody &amp; Private Keys</h3>
            <p>
              <strong>Your wallet is yours. Its private key is yours to manage.</strong>{' '}
              Whether you connected an existing Klever wallet or Klyx generated one for you at
              email signup, the private key controls every KLV, every profile, and every
              on-chain asset associated with that wallet.
            </p>
            <p>
              <strong>Never share your private key or seed phrase with anyone &mdash; including Klyx staff.</strong>{' '}
              Anyone who obtains it can transfer your funds and profiles instantly and irreversibly.
              Klyx will never ask for your private key or seed phrase under any circumstances. Any
              message, email, or user claiming to be Klyx support asking for these is a scam.
            </p>
            <p>
              If you signed up with email, we generated a wallet for you as a convenience and stored
              the key server-side under encryption. You may export it at any time, and we strongly
              recommend that you do &mdash; once exported and self-custodied, your assets are safe
              from any Klyx-side outage, breach, or shutdown.
            </p>
            <p>
              Klyx cannot recover, reset, or reverse transactions signed by a private key that you
              or a third party controls. Loss of the key means permanent loss of the assets it
              controls. This is inherent to public blockchains and not a limitation of Klyx.
            </p>
          </section>

          <section>
            <h3>5. KLYX Tokens &amp; Transactions</h3>
            <p>
              KLYX tokens are the native utility token of the Platform. All token transactions occur
              on the Klever blockchain and are subject to network fees (gas). The Platform charges a
              2% fee on profile trades. Token values are volatile and Klyx makes no representations
              regarding future value.
            </p>
          </section>

          <section>
            <h3>6. Prohibited Conduct</h3>
            <p>You agree not to:</p>
            <ul>
              <li>Use the Platform for any unlawful purpose or in violation of any regulations</li>
              <li>Attempt to manipulate or exploit the smart contract or Platform systems</li>
              <li>Harass, threaten, or abuse other users</li>
              <li>Upload or transmit content that is illegal, harmful, or infringes third-party rights</li>
              <li>Create multiple accounts to circumvent bans or restrictions</li>
              <li>Use automated scripts or bots without prior written consent from Klyx</li>
            </ul>
          </section>

          <section>
            <h3>7. NFT Profile Ownership</h3>
            <p>
              When you purchase an NFT profile, you acquire ownership of the token on the blockchain.
              This does not grant rights to the underlying persona, intellectual property, or real
              identity of the profile creator. Klyx retains the right to delist profiles that violate
              these Terms.
            </p>
          </section>

          <section>
            <h3>8. Privacy &amp; Data</h3>
            <p>
              Klyx collects and processes personal data as described in our Privacy Policy. By using
              the Platform, you consent to such processing. Blockchain transactions are public and
              cannot be made private. We use industry-standard encryption for sensitive off-chain data.
            </p>
          </section>

          <section>
            <h3>9. Platform Availability</h3>
            <p>
              Klyx does not guarantee uninterrupted access to the Platform. We reserve the right to
              suspend, modify, or discontinue any feature at any time. Scheduled and unscheduled
              maintenance may affect service availability.
            </p>
          </section>

          <section>
            <h3>10. Limitation of Liability</h3>
            <p>
              To the fullest extent permitted by law, Klyx and its affiliates shall not be liable for
              any indirect, incidental, special, or consequential damages arising from your use of
              the Platform, including loss of tokens, data, or profits, even if advised of the
              possibility of such damages.
            </p>
          </section>

          <section>
            <h3>11. Dispute Resolution</h3>
            <p>
              Any dispute arising from these Terms shall be resolved through binding arbitration in
              accordance with applicable laws. You waive any right to participate in a class-action
              lawsuit against Klyx.
            </p>
          </section>

          <section>
            <h3>12. Changes to Terms</h3>
            <p>
              Klyx reserves the right to update these Terms at any time. When material changes are
              made, you will be required to review and re-accept the updated Terms before continuing
              to use the Platform. Continued use after acceptance constitutes agreement to the
              revised Terms.
            </p>
          </section>

          <section>
            <h3>13. Contact</h3>
            <p>
              For questions regarding these Terms, please contact the Klyx team through the Platform's
              official support channels.
            </p>
          </section>

          {!hasScrolledToBottom && (
            <div className="terms-modal__scroll-hint">
              Scroll down to read all terms
            </div>
          )}
        </div>

        <div className="terms-modal__footer">
          <label className={`terms-modal__checkbox ${!hasScrolledToBottom ? 'terms-modal__checkbox--disabled' : ''}`}>
            <input
              type="checkbox"
              checked={agreed}
              onChange={e => setAgreed(e.target.checked)}
              disabled={!hasScrolledToBottom || isSubmitting}
            />
            <span>I have read and agree to the Terms &amp; Conditions</span>
          </label>

          {error && (
            <p className="terms-modal__error">{error}</p>
          )}

          <button
            className="terms-modal__accept-btn"
            onClick={handleAccept}
            disabled={!agreed || isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Continue to Klyx'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TermsModal;
